
import React, { useState, useEffect } from 'react';
import { User, UserRole, Reservation, Gym, Zone } from './types';
import { supabase } from './supabase';
import GymPortal from './components/GymPortal';
import ClientPortal from './components/ClientPortal';
import Login from './components/Login';
import SetPasswordModal from './components/SetPasswordModal';
import SuperAdminModal from './components/SuperAdminModal';
import { LogOut as LogOutIcon, Loader2 as LoaderIcon, MessageSquare, ShieldAlert, ShieldCheck } from 'lucide-react';

const App: React.FC = () => {
  const [session, setSession] = useState<any>(null);
  const [profile, setProfile] = useState<User | null>(null);
  const [gyms, setGyms] = useState<Gym[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [loading, setLoading] = useState(true);
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showSetPassword, setShowSetPassword] = useState(false);
  const [showSuperAdmin, setShowSuperAdmin] = useState(false);

  const isSuperAdmin = profile?.email === 'gonzalolanis@gmail.com';

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      
      // Check for recovery hash or dedicated path
      if (window.location.hash.includes('type=recovery') || window.location.pathname === '/reset-password') {
        setShowSetPassword(true);
      }

      if (session) fetchInitialData(session);
      else setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      
      if (event === 'PASSWORD_RECOVERY' || window.location.pathname === '/reset-password') {
        setShowSetPassword(true);
      }

      if (session) fetchInitialData(session);
      else {
        setProfile(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchInitialData = async (currentSession: any) => {
    setLoading(true);
    await Promise.all([
      fetchZones(),
      fetchGyms(),
      fetchProfile(currentSession.user.id, currentSession.user.email, currentSession.user.user_metadata),
      cleanupOldMessages()
    ]);
    setLoading(false);
  };

  const cleanupOldMessages = async () => {
    try {
      const tenDaysAgo = new Date();
      tenDaysAgo.setDate(tenDaysAgo.getDate() - 10);
      const isoString = tenDaysAgo.toISOString();

      const { error } = await supabase
        .from('messages')
        .delete()
        .lt('created_at', isoString);

      if (error) console.error('Error cleaning up old messages:', error);
    } catch (err) {
      console.error('Failed to cleanup old messages:', err);
    }
  };

  const fetchZones = async () => {
    const { data } = await supabase.from('zones').select('*');
    if (data) setZones(data.map(z => ({ id: z.id, name: z.name })));
  };

  const fetchGyms = async () => {
    const { data } = await supabase.from('gyms').select('*');
    if (data) {
      setGyms(data.map(g => ({
        id: g.id,
        name: g.name,
        zoneId: g.zone_id,
        address: g.address,
        description: g.description,
        capacityPerSlot: g.capacity_per_slot,
        image: g.image_url
      })));
    }
  };

  const fetchProfile = async (userId: string, email?: string, metadata?: any) => {
    try {
      let { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (!data) {
        const { data: newData, error: insertError } = await supabase
          .from('profiles')
          .insert([{ 
            id: userId, 
            full_name: metadata?.full_name || 'Nuevo Socio',
            role: metadata?.role || UserRole.CLIENT
          }])
          .select()
          .single();
        
        if (insertError) throw insertError;
        data = newData;
      }
      
      let currentHomeGymId = data.home_gym_id;
      let currentHomeZoneId = null;
      let currentBenefitActive = data.benefit_active;

      if (data.role === UserRole.CLIENT && email) {
        // OBTENEMOS LA ZONA REAL DESDE AUTHORIZED_EMAILS
        const { data: authData } = await supabase
          .from('authorized_emails')
          .select('gym_id, zone_id, is_active')
          .eq('email', email.toLowerCase().trim())
          .maybeSingle();

        if (authData) {
          currentHomeGymId = authData.gym_id;
          currentHomeZoneId = authData.zone_id;
          currentBenefitActive = authData.is_active;

          if (data.home_gym_id !== authData.gym_id) {
             await supabase.from('profiles').update({ home_gym_id: authData.gym_id }).eq('id', userId);
          }
        }
      }
      
      const mappedUser: User = {
        id: data.id,
        email: email || '',
        name: data.full_name,
        role: data.role as UserRole,
        gymId: data.gym_id,
        homeGymId: currentHomeGymId,
        homeZoneId: currentHomeZoneId, 
        benefitActive: currentBenefitActive,
        weeklyPassesUsed: 0
      };
      
      setProfile(mappedUser);
      fetchReservations(mappedUser);
    } catch (err) {
      console.error("Error fetching/creating profile:", err);
    }
  };

  // ✅ Suscripción en tiempo real para que las reservas se actualicen solas
  useEffect(() => {
    if (!profile) return;

    const channel = supabase
      .channel('reservations_realtime')
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'reservations' 
      }, () => {
        fetchReservations(profile);
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [profile]);

  const fetchUnreadCount = async (userId: string, role?: UserRole) => {
    if (!role) return;
    
    try {
      const { data, error } = await supabase
        .from('messages')
        .select('id')
        .eq(role === UserRole.GYM ? 'gym_id' : 'client_id', userId)
        .neq('is_read', true)
        .eq('sender_role', role === UserRole.GYM ? 'CLIENT' : 'GYM');

      if (!error) {
        setUnreadCount(data?.length || 0);
      }
    } catch (err) {
      console.error('Error fetching unread count:', err);
    }
  };

  // Real-time subscription for unread messages
  useEffect(() => {
    if (!profile) return;

    const targetId = profile.role === UserRole.GYM ? profile.gymId : profile.id;
    if (!targetId) return;

    // Initial fetch
    fetchUnreadCount(targetId, profile.role);

    // Subscribe to any change in the messages table
    const channel = supabase
      .channel('global_notifications')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'messages'
        },
        () => {
          // Whenever ANY message changes, we re-fetch the count for the current user
          // This is the most reliable way to ensure the count is always correct
          fetchUnreadCount(targetId, profile.role);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [profile]);

  const fetchReservations = async (currentUser: User) => {
    const filterKey = currentUser.role === UserRole.GYM ? 'gym_id' : 'client_id';
    const { data, error } = await supabase
      .from('reservations')
      .select('*')
      .eq(filterKey, currentUser.id);

    if (!error && data) {
      setReservations(data.map(r => ({
        id: r.id,
        clientId: r.client_id,
        gymId: r.gym_id,
        slotId: r.slot_id,
        date: r.reservation_date,
        status: r.status,
        qrCode: r.qr_code,
        createdAt: r.created_at
      })));
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
  };

  if (showSetPassword) {
    return <SetPasswordModal onComplete={() => setShowSetPassword(false)} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <LoaderIcon size={40} className="animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-slate-500 font-black uppercase text-[10px] tracking-widest">Sincronizando red...</p>
        </div>
      </div>
    );
  }
  if (!session || !profile) {
    return (
      <div className="min-h-screen flex flex-col bg-slate-50">
        <Login onLogin={() => {}} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-bg text-white pb-20 md:pb-0 font-sans">
      <nav className="bg-dark-surface/80 backdrop-blur-xl border-b border-white/5 px-4 py-3 sm:px-6 sm:py-4 flex justify-between items-center sticky top-0 z-40">
        <div className="flex items-center gap-3 sm:gap-4 relative">
          <div className="relative">
            <img 
              src="https://hshqshiznfwasvgtcjaj.supabase.co/storage/v1/object/sign/logo/logo.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV9iYzQ1ODVmNy01ZWVlLTQ0MzctYjk3Mi1lNGYyYzQ0ZjM4ZmUiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJsb2dvL2xvZ28ucG5nIiwiaWF0IjoxNzcxNTU0ODk2LCJleHAiOjQ4OTM2MTg4OTZ9.fxSBsODmtEQ_0T3sewE7hBTPcKBjlsvo3qJQPdxZCOU" 
              alt="Logo" 
              className="w-16 h-16 sm:w-24 sm:h-24 object-contain brightness-110"
              referrerPolicy="no-referrer"
            />
          </div>
          <span className="font-display text-xl sm:text-2xl uppercase tracking-tighter hidden xs:inline">GymBrotherhood</span>
        </div>
        
        <div className="flex items-center gap-4 sm:gap-6">
          {isSuperAdmin && (
            <button 
              onClick={() => setShowSuperAdmin(true)}
              className="p-2 sm:p-3 text-brand hover:bg-brand/10 rounded-xl transition-all flex items-center gap-2"
              title="Super Admin"
            >
              <ShieldCheck size={20} />
              <span className="hidden md:inline text-[10px] font-bold uppercase tracking-widest">Admin</span>
            </button>
          )}
          
          <div className="text-right hidden sm:block">
            <p className="text-sm font-bold text-white leading-none mb-1">{profile.name}</p>
            <p className="text-[9px] text-brand uppercase tracking-[0.2em] font-bold">
               {profile.role === UserRole.GYM ? 'Staff Terminal' : 'Athlete Mode'}
            </p>
          </div>
          <button 
            onClick={handleLogout} 
            className="p-2 sm:p-3 text-white/20 hover:text-brand transition-all hover:bg-white/5 rounded-xl sm:rounded-2xl"
          >
            <LogOutIcon size={20} className="sm:w-[22px] sm:h-[22px]" />
          </button>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto p-4 sm:p-6 md:p-12">
        {showSuperAdmin && (
          <SuperAdminModal onClose={() => setShowSuperAdmin(false)} gyms={gyms} />
        )}
        
        {profile.role === UserRole.GYM ? (
          <GymPortal user={profile} reservations={reservations} setReservations={setReservations} gyms={gyms} zones={zones} />
        ) : (
          <ClientPortal user={profile} reservations={reservations} setReservations={setReservations} gyms={gyms} zones={zones} />
        )}
      </main>
    </div>
  );
};

export default App;
